import { ScreenHeader } from '@/components/layout/screen-header';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { toast } from '@/components/ui/toast';
import { SettingsRegistryView } from '@/features/settings';
import Constants from 'expo-constants';
import { Image } from 'expo-image';
import React, { useCallback, useState } from 'react';
import { Platform, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

interface SettingsScreenProps {
  onSignOut?: () => void;
}

/**
 * Native-only row that empties expo-image's disk and memory caches. On web the
 * browser owns the image cache, so the row is not shown.
 */
function ClearImageCacheRow() {
  const [isClearing, setIsClearing] = useState(false);

  const handleClear = useCallback(async () => {
    setIsClearing(true);
    try {
      await Promise.all([Image.clearDiskCache(), Image.clearMemoryCache()]);
      toast.success('Image cache cleared');
    } catch (error) {
      console.error('Failed to clear image cache:', error);
      toast.error('Could not clear image cache', { description: 'Please try again.' });
    } finally {
      setIsClearing(false);
    }
  }, []);

  return (
    <Button
      variant="outline"
      fullWidth
      className="mt-4"
      onPress={handleClear}
      loading={isClearing}
      accessibilityLabel="Clear image cache"
    >
      Clear Image Cache
    </Button>
  );
}

/**
 * The settings screen shell. All rows are declared in the settings registry and
 * rendered generically by `SettingsRegistryView` — each row self-fetches through
 * its hook, so this screen only owns the chrome (header, sign out, version) and
 * the native-only image cache action.
 */
export function SettingsScreen({ onSignOut }: SettingsScreenProps) {
  return (
    <SafeAreaView className="flex-1" edges={['top']}>
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="px-4 pb-8"
      >
        <ScreenHeader title="Settings" showBackButton />

        <SettingsRegistryView />

        {Platform.OS !== 'web' && <ClearImageCacheRow />}

        {onSignOut && (
          <Button
            variant="secondary"
            fullWidth
            className="mt-4"
            onPress={onSignOut}
            accessibilityLabel="Sign out"
          >
            Sign Out
          </Button>
        )}

        <Text className="text-center text-xs mt-6 text-muted-foreground">
          Version {Constants.expoConfig?.version ?? '0.0.0'}
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}
